import * as React from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { useExperiment } from '@coursera-web/statsig';

import { breakpoints } from '@coursera/cds-core';

import { CoachStateProvider } from 'bundles/unified-common/providers/CoachStateProvider';
import { TwoPanelChatRouteProvider } from 'bundles/unified-common/providers/TwoPanelChatRouteProvider';
import { useIsCourseDataLoading } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffCourseContext';
import { useIsS12nDataLoading } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffSpecializationContext';
import useIsDesktop from 'bundles/unified-description-page-common/hooks/useIsDesktop';

const EXPERIMENT_NAME = 'consumer-xdp-two-panel-convo-disco';
const PARAM_NAME = 'chat-panel-location';
const CHAT_ON_LEFT = 'left';
const CHAT_ON_RIGHT = 'right';
const NO_CHAT_PANEL = 'none';
// md === 1024, the exact complement of useIsDesktop's down('sm') (<1024).
const DESKTOP_WIDTH_QUERY = `(min-width: ${breakpoints.values.md}px)`;

export type TwoPanelConversationalDiscoveryExperimentContextType = {
  twoPanelConversationalDiscoveryEnabled: boolean;
  chatOnLeft: boolean;
  chatOnRight: boolean;
};

export const TwoPanelConversationalDiscoveryExperimentContext =
  createContext<TwoPanelConversationalDiscoveryExperimentContextType>({
    twoPanelConversationalDiscoveryEnabled: false,
    chatOnLeft: false,
    chatOnRight: false,
  });

function TwoPanelConversationalDiscoveryExperimentProvider({ children }: React.PropsWithChildren) {
  const isS12nDataLoading = useIsS12nDataLoading();
  const isCourseDataLoading = useIsCourseDataLoading();

  const isDesktop = useIsDesktop(); // UA-seeded → SSR↔CSR-stable for the >=1024 population

  // Real viewport width: read SYNCHRONOUSLY at first client render (lazy-init seed) and kept LIVE via a
  // matchMedia `change` listener (same shape as launched-lab/hooks/useIsMobileViewport.tsx).
  //  - Synchronous seed: useIsDesktop is UA-seeded, so a desktop UA reports desktop at first paint even
  //    on a <1024px window and only flips after useMediaQuery settles — that flash is what logged the
  //    spurious <1024px exposure. matchMedia gives the true width immediately, so the seed reads false
  //    and the flash is excluded.
  //  - Listener: a session that loads <1024px and later widens to >=1024px flips this to true →
  //    re-render → the gate below logs its single (deduped) exposure. The log fires in this parent's
  //    render BEFORE the heavy treatment subtree mounts, so the resize exposure stays on the symmetric
  //    render-time path and cannot reintroduce the SRM. SSR-guarded; server logging is a no-op anyway.
  const [isDesktopWidth, setIsDesktopWidth] = useState(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia(DESKTOP_WIDTH_QUERY).matches
  );
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mql = window.matchMedia(DESKTOP_WIDTH_QUERY);
    const handler = (e: MediaQueryListEvent) => setIsDesktopWidth(e.matches);
    mql.addEventListener?.('change', handler);
    return () => mql.removeEventListener?.('change', handler);
  }, []);

  // Read-only: value + readiness, never auto-logs (disableExposureLog always true → only the harmless
  // non_exposed_checks counter). .get() drives the SSR-stable layout exactly as today.
  const experiment = useExperiment(EXPERIMENT_NAME, { disableExposureLog: true });
  // A real assignment for THIS client ⇔ reason ends with ':Recognized'. 'Bootstrap:Unrecognized',
  // 'Uninitialized', 'NoValues' mean the client has no value yet — logging then records a Default
  // exposure and biases the sample (the non-variant bias).
  const isAssignmentRecognized = experiment.details.reason.endsWith(':Recognized');
  const location = isDesktop ? experiment.get(PARAM_NAME, NO_CHAT_PANEL) : NO_CHAT_PANEL;

  // Log the exposure at RENDER TIME (useExperiment logs synchronously in its memo, here in the parent
  // BEFORE the heavy/throwing treatment subtree is created) so the asymmetric treatment render can't
  // delay or suppress it — that asymmetry is the per-variant SRM. All three gates are
  // variant-independent, so exposure stays balanced across none/left/right; the SDK dedupes per session
  // → exactly one exposure.
  //  - isAssignmentRecognized: keeps Default/Uninitialized/NoValues out of the sample (non-variant bias).
  //  - isDesktop: hard-excludes mobile/tablet UA (and the eventual <1024px width).
  //  - isDesktopWidth: excludes the desktop-UA <1024px first-paint flash; flips true on narrow→wide
  //    resize so that session logs its one exposure.
  useExperiment(EXPERIMENT_NAME, {
    disableExposureLog: !(isDesktop && isDesktopWidth && isAssignmentRecognized),
  });

  const value = useMemo(() => {
    const chatOnLeft = location === CHAT_ON_LEFT;
    const chatOnRight = location === CHAT_ON_RIGHT;
    return {
      twoPanelConversationalDiscoveryEnabled: chatOnLeft || chatOnRight,
      chatOnLeft,
      chatOnRight,
    };
  }, [location]);

  return (
    <TwoPanelConversationalDiscoveryExperimentContext.Provider value={value}>
      {value.twoPanelConversationalDiscoveryEnabled ? (
        <CoachStateProvider coachOpen={true} isContextConfigLoading={isS12nDataLoading || isCourseDataLoading}>
          <TwoPanelChatRouteProvider>{children}</TwoPanelChatRouteProvider>
        </CoachStateProvider>
      ) : (
        children
      )}
    </TwoPanelConversationalDiscoveryExperimentContext.Provider>
  );
}

export const useTwoPanelConversationalDiscoveryExperiment = () => {
  return useContext(TwoPanelConversationalDiscoveryExperimentContext);
};

export default TwoPanelConversationalDiscoveryExperimentProvider;
